/**
 * ItemPalette — modal that replaces the cluster of "+ Btn / + LFO / + Grid /
 * + Seq / + SD" buttons in the Header. One `+` opens this; user picks a
 * kind from a grid of tiles, each showing the kind's name + a thumbnail
 * preview of what one looks like.
 *
 * Thumbnails are inline SVG mock-ups of each control. They're not the
 * actual render components — keeping them as small static shapes means
 * the palette renders instantly and doesn't pull on bridge state. If a
 * control's visual language changes, update the thumbnail here alongside
 * the real component.
 *
 * Click-outside + Esc close. Selecting a kind adds the item via the
 * matching factory and closes the modal in one motion — except "Guitar FX
 * Preset" (DEC-152), which steps into a pick-your-pedal sub-flow first
 * whenever at least one pedal is registered (see the flow doc above
 * `ItemPalette` below).
 */

import { For, Show, createSignal, onMount, onCleanup, type JSX } from 'solid-js';
import { Portal } from 'solid-js/web';
import {
  addButton,
  addLfo,
  addButtonGrid,
  addSequencer,
  addStreamDeck,
  addMacro,
  addStatusLed,
  addFader,
  addRadial,
  addLabel,
  addPresetChangeForPedal,
  addGuitarFxButton,
  addSnapshotChange,
  addRigStateButton,
  addLightColor,
  addLightLevel,
  addLightFx,
  addBlackout,
  addOpVisualGrid,
  addOpVisualTrigger,
  addAudioPads,
  addXYPad,
  addTimelineLauncher,
  addPiano,
  addTranspose,
  addCapo,
  addRecQuant,
  addSceneCopy,
  addWavesAutotune,
  addMixer,
  addDisplay,
  addAbletonControl,
  addObsControl,
  addProgramControl,
  addLightPresetControl,
  addHueSceneControl,
  addAbletonDevice,
  addAbletonRow,
  addAndAnnounce, setSpawnPoint,
} from '../layout/store.ts';
import { ABLETON_CONTROLS, freshAbletonStep, type AbletonControlChoice } from '../ableton/rows.ts';
import { activePedals, updatePedal, pedalModel, type GuitarPedal } from '../layout/projectStore.ts';
import { requestWavesChannel } from '../autotune/conflict.ts';

interface ItemPaletteProps {
  onClose: () => void;
  /** Render inside Navigate's "Add a control" pane (2026-10-05): no overlay, no own Esc. */
  embedded?: boolean;
}

interface PaletteEntry {
  id: string;
  name: string;
  description: string;
  /** SVG markup for the thumbnail body (sized to fit ~180×100 viewport). */
  Thumb: () => JSX.Element;
  add: () => void;
  /** Reactive: a truthy string greys the tile out and REPLACES `description`
   *  with it, disabling the click (DEC-152, 2026-08-04) — for a tile that
   *  would otherwise spawn an item guaranteed to render dead. Absent /
   *  returning `undefined` = the tile behaves exactly as every other one. */
  disabledReason?: () => string | undefined;
}

const ENTRIES: PaletteEntry[] = [
  {
    id: 'button',
    name: 'Button',
    description: 'Single MIDI toggle',
    Thumb: ButtonThumb,
    add: () => addButton(),
  },
  {
    id: 'buttongrid',
    name: 'Button Grid',
    description: 'Mutually-exclusive radio strip',
    Thumb: ButtonGridThumb,
    add: () => addButtonGrid(1, 10),
  },
  {
    id: 'lfo',
    name: 'LFO',
    description: 'Beat-locked modulator',
    Thumb: LfoThumb,
    add: () => addLfo(),
  },
  {
    id: 'sequencer',
    name: 'Sequencer',
    description: '4-lane step sequencer',
    Thumb: SequencerThumb,
    add: () => addSequencer(),
  },
  {
    id: 'macro',
    name: 'Macro',
    description: 'Shape-driven beat-locked sweep',
    Thumb: MacroThumb,
    add: () => addMacro(),
  },
  {
    id: 'streamdeck',
    name: 'Stream Deck',
    description: 'Fire an Elgato action (OBS, Hue, hotkey) via OPBridge',
    Thumb: StreamDeckThumb,
    add: () => addStreamDeck(),
  },
  {
    id: 'statusLed',
    name: 'Status LED',
    description: 'Read-only indicator, lights on incoming MIDI',
    Thumb: StatusLedThumb,
    add: () => addStatusLed(),
  },
  {
    id: 'fader',
    name: 'Fader',
    description: 'Continuous CC on a vertical/horizontal slider',
    Thumb: FaderThumb,
    add: () => addFader(),
  },
  {
    id: 'radial',
    name: 'Radial',
    description: 'Rotary knob, one CC. Drag up/down; double-tap resets',
    Thumb: RadialThumb,
    add: () => addRadial(),
  },
  {
    id: 'label',
    name: 'Label',
    description: 'Display text. Sends nothing, claims no CC',
    Thumb: LabelThumb,
    add: () => addLabel(),
  },
  {
    // Generalized from "Line 6 Helix" (DEC-148) once the pedal registry
    // (DEC-152) made a Helix one device among possibly several. `add()`
    // always opens the pick-your-pedal flow — reachable only when a pedal
    // is registered, since `disabledReason` greys the tile out otherwise
    // (2026-08-04 operator ask: no more spawning a guaranteed-dead tile).
    id: 'guitarFxPreset',
    name: 'Guitar FX Preset',
    description: 'Recall a preset on a registered pedal (Helix or Custom)',
    Thumb: PresetChangeThumb,
    // Never actually called — pick() intercepts this id before reaching
    // entry.add() and opens the pick-your-pedal flow instead (it needs
    // setPedalStep, which only exists inside the component below, not at
    // this module-level array).
    add: () => {},
    disabledReason: () => (activePedals().length === 0
      ? 'Add a guitar pedal in Pedals and devices to activate this item'
      : undefined),
  },
  {
    // Sits next to the preset tile and is named for the DEVICE's own word
    // (DEC-151). "Snapshot" is what is printed on the pedal and what the
    // player already says out loud, so it needs no explanation — and the
    // description carries the one thing that is not obvious: this does NOT
    // reload the preset, which is the entire reason to reach for it.
    //
    // Same greying-out logic as Guitar FX Preset — a snapshot only means
    // anything on a Helix, so this gates on a HELIX pedal specifically
    // rather than any pedal (a Custom-only rig still can't use this tile).
    id: 'snapshotChange',
    name: 'Helix Snapshot',
    description: 'Switch snapshot without reloading the preset',
    Thumb: SnapshotChangeThumb,
    add: () => addSnapshotChange(),
    disabledReason: () => (activePedals().some((p) => p.type === 'helix')
      ? undefined
      : 'Add a Line 6 Helix in Pedals and devices to activate this item'),
  },
  {
    id: 'rigStateButton',
    name: 'Rig State',
    description: 'Recall one look from a Rig State bank',
    Thumb: RigStateButtonThumb,
    add: () => addRigStateButton(),
  },
  // ─── Lighting (PLAN.md QUEUED "DMX / lighting v1", DEC-013) ───
  // Named for what the operator WANTS, never for the protocol. "Light
  // Colour", not "DMX Set State"; the word DMX does not appear on a tile,
  // in a description, or anywhere else an operator reads. That is DEC-014's
  // capability moat showing up in the vocabulary, and the same move DEC-152
  // makes by calling a profile "Line 6 Helix".
  //
  // NOT greyed out when the rig is empty, deliberately - unlike the Helix
  // tiles, which gate on a pedal existing. A lighting control with no rig is
  // a control whose TARGET is empty, and it says so on its own face with a
  // specific reason ("no lights in rig"). Greying the tile would hide the
  // feature from the one person who most needs to discover it: someone who
  // has lights but has not patched them yet.
  {
    id: 'lightColor',
    name: 'Light Colour',
    description: 'Set a colour on one light, a group, or everything',
    Thumb: LightColorThumb,
    add: () => addLightColor(),
  },
  {
    id: 'lightLevel',
    name: 'Light Level',
    description: 'Fader for brightness, without touching the colour',
    Thumb: LightLevelThumb,
    add: () => addLightLevel(),
  },
  {
    id: 'lightFx',
    name: 'Light FX',
    description: 'Strobe, fire, rainbow - some of them on the beat',
    Thumb: LightFxThumb,
    add: () => addLightFx(),
  },
  {
    id: 'lightPreset',
    name: 'Light Preset',
    description: 'Play a ready-made light show - chases, pulses, strobes - and stop it again',
    Thumb: LightPresetThumb,
    add: () => addLightPresetControl(),
  },
  {
    id: 'hueScene',
    name: 'Hue Scene',
    description: 'Recall a scene from the Hue app - the whole room at once - and light up while it is on',
    Thumb: HueSceneThumb,
    add: () => addHueSceneControl(),
  },
  {
    id: 'blackout',
    name: 'Blackout',
    description: 'Kill every light at once',
    Thumb: BlackoutThumb,
    add: () => addBlackout(),
  },
  // ─── OPVisual (2026-09-24, .notepads/opvisual-control-plan.md) ───
  // The Grid is the "OPVisual page": drop it on its own page and it fills
  // the canvas. The Trigger is one cell / column / action on a button, for
  // performance pages. Both find the OPVisual computer on their own when
  // there is only one it could be.
  {
    id: 'opvisualGrid',
    name: 'OPVisual Grid',
    description: 'Fire OPVisual cells and columns, and mix its layers',
    Thumb: OpVisualGridThumb,
    add: () => addOpVisualGrid(),
  },
  {
    id: 'opvisualTrigger',
    name: 'OPVisual Trigger',
    description: 'One OPVisual cell, column or action on a button',
    Thumb: OpVisualTriggerThumb,
    add: () => addOpVisualTrigger(),
  },
  // Audio pads (2026-10-13): play files from Navigate > Audio on a computer's output, like Ableton's drum rack.
  {
    id: 'audioPad',
    name: 'Audio Pad',
    description: 'Play a one-shot, loop or song on a press',
    Thumb: AudioPadThumb,
    add: () => addAudioPads(1, 1),
  },
  {
    id: 'audioPads',
    name: 'Audio Pads',
    description: 'A soundboard: a grid of pads, several playing at once',
    Thumb: AudioPadsThumb,
    add: () => addAudioPads(2, 4),
  },
  {
    id: 'xypad',
    name: 'XY Pad',
    description: 'Two-CC 2D touch surface (Kaoss-style)',
    Thumb: XYPadThumb,
    add: () => addXYPad(),
  },
  {
    id: 'timelineLauncher',
    name: 'Timeline Launcher',
    description: 'Launch a timeline or any of its sections',
    Thumb: TimelineLauncherThumb,
    add: () => addTimelineLauncher(),
  },
  {
    id: 'piano',
    name: 'Piano',
    description: '1-3 octaves of MIDI note keys',
    Thumb: PianoThumb,
    add: () => addPiano(),
  },
  // ─── Ableton (grouped 2026-10-18, DEC-291) ───
  // Everything that drives Live sits together, Ableton Control first: it is
  // the one most reached for, and the Rec Quantize tile folded into it.
  {
    // Opens a pick-what-to-control step (tempo, transport, quantize...) with
    // One button or Full row on each, rather than dropping a tempo button
    // and leaving the operator to find the rest in the Inspector. `add` is
    // the no-choice path (an old caller): one tempo button, as before.
    // pick() intercepts the tile.
    id: 'abletonControl',
    name: 'Ableton Control',
    description: 'Tempo, transport, quantize, metronome: one button or a full row',
    Thumb: AbletonControlThumb,
    add: () => addAbletonControl(),
  },
  {
    id: 'abletonDevice',
    name: 'Ableton Device',
    description: 'Switch one named device on one track on or off, lit from Live itself',
    Thumb: AbletonDeviceThumb,
    add: () => addAbletonDevice(),
  },
  {
    id: 'mixer',
    name: 'Mixer',
    description: 'Ableton channel-strip bank -> OP-AbletonMixer via OSC',
    Thumb: MixerThumb,
    add: () => addMixer(),
  },
  {
    id: 'sceneCopy',
    name: 'Scene Copy/Paste',
    description: "Copy a scene's clips into another, without renumbering scenes",
    Thumb: SceneCopyThumb,
    add: () => addSceneCopy(),
  },
  {
    id: 'transpose',
    name: 'Transpose',
    description: 'Semitone row → Ableton via OP-AbletonTranspose (OSC)',
    Thumb: TransposeThumb,
    add: () => addTranspose(),
  },
  {
    id: 'capo',
    name: 'Capo',
    description: 'Capo-fret view of the transpose (drop-tuning aware)',
    Thumb: CapoThumb,
    add: () => addCapo(),
  },
  {
    id: 'wavesAutotune',
    name: 'Waves Autotune',
    description: 'Key + MIN/MAJ/Chrom scale selector -> Waves via MIDI',
    Thumb: WavesAutotuneThumb,
    add: () => {
      const it = addWavesAutotune();
      // Surface a Matrix conflict immediately if the default channel's
      // fixed CCs are already claimed (even on ch2). Cancel removes it.
      if (it) requestWavesChannel(it, it.channel, 'add');
      return it;
    },
  },
  {
    id: 'display',
    name: 'Display',
    description: 'A glance readout: the timeline now playing, tempo, transpose, a Helix preset and more. Sends nothing',
    Thumb: DisplayThumb,
    add: () => addDisplay(),
  },
  {
    id: 'obsScene',
    name: 'OBS Scene',
    description: 'Switch OBS to one scene, and light up while that scene is live',
    Thumb: ObsSceneThumb,
    add: () => addObsControl(),
  },
  {
    id: 'runProgram',
    name: 'Run Program',
    description: 'Start a program on any computer in your rig, and light up while it runs',
    Thumb: RunProgramThumb,
    add: () => addProgramControl(),
  },
];

/**
 * "Guitar FX Preset" pick-your-pedal flow (DEC-152), grafted onto this
 * modal rather than a separate one so the palette stays a single click ->
 * single close for every OTHER tile. Two steps, only reachable when the
 * operator has registered at least one pedal (zero pedals = the tile
 * instant-adds an unconfigured `presetChange` exactly as it always did):
 *
 *   'pedal'  — which registered pedal? A Helix pick binds a `presetChange`
 *              tile directly to it and closes. A Custom pick has no
 *              multi-message protocol, so it falls through to step 2.
 *   'preset' — name (or pick a previously-named) PC for a Custom pedal.
 *              Names live on `GuitarPedal.presetNames`, typed once and
 *              reused — the same idea as `helixSetlistOverride` in
 *              `helix/library.ts`, because a Custom pedal has no USB read
 *              to pull real names from.
 */
type PedalPickStep = 'pedal' | 'preset';

export function ItemPalette(props: ItemPaletteProps) {
  const [pedalStep, setPedalStep] = createSignal<PedalPickStep | null>(null);
  // The Ableton Control step (DEC-291): what to control, then One button or
  // Full row. A step of this panel, like the pedal flow, so the palette
  // stays one window.
  const [abletonPick, setAbletonPick] = createSignal(false);
  const [pickedPedal, setPickedPedal] = createSignal<GuitarPedal | null>(null);
  const [newPresetName, setNewPresetName] = createSignal('');
  const [newPresetPc, setNewPresetPc] = createSignal('');

  function resetPedalFlow(): void {
    setPedalStep(null);
    setAbletonPick(false);
    setPickedPedal(null);
    setNewPresetName('');
    setNewPresetPc('');
  }

  function closeAll(): void {
    resetPedalFlow();
    props.onClose();
  }

  /** Esc steps back one level inside the pedal flow before it closes the
   *  whole palette — mirrors the Back button so keyboard and touch agree. */
  function onKey(e: KeyboardEvent) {
    if (e.key !== 'Escape') return;
    if (abletonPick()) { setAbletonPick(false); return; }
    if (pedalStep() === 'preset') { setPedalStep('pedal'); setPickedPedal(null); return; }
    if (pedalStep() === 'pedal') { resetPedalFlow(); return; }
    props.onClose();
  }
  onMount(() => { if (!props.embedded) window.addEventListener('keydown', onKey); });
  onCleanup(() => window.removeEventListener('keydown', onKey));

  function pick(entry: PaletteEntry) {
    // Defensive: the tile's own `disabled` attribute already blocks this
    // click in the UI. Guard here too so a disabled entry can never spawn
    // anything even if that ever drifts out of sync.
    if (entry.disabledReason?.()) return;
    if (entry.id === 'guitarFxPreset') {
      // Steps into the pick-your-pedal flow instead of closing immediately
      // — every other tile closes in one motion via entry.add() below.
      setPedalStep('pedal');
      return;
    }
    if (entry.id === 'abletonControl') {
      setAbletonPick(true);
      return;
    }
    addAndAnnounce(entry.add);
    closeAll();
  }

  /** Add the picked Ableton Control in the picked form, then close. */
  function addAbleton(choice: AbletonControlChoice, form: 'button' | 'row'): void {
    if (form === 'row' && choice.row !== null) {
      const row = choice.row;
      addAndAnnounce(() => { if (row === 'recQuant') addRecQuant(); else addAbletonRow(row); });
    } else {
      addAndAnnounce(() => addAbletonControl(undefined, freshAbletonStep(choice.stepType)));
    }
    closeAll();
  }

  // ─── DRAG A TILE ONTO THE CANVAS (2026-10-10) ─────────────────────
  // Pick a tile up and put it down where the control should go. Mouse: drag
  // past a few px. Touch: press and hold, then drag - a plain swipe still
  // scrolls the palette. While carried, the palette steps out of the way
  // (hidden, not unmounted: a touch whose target leaves the DOM stops
  // reaching window listeners) and a chip follows the pointer. Let go over
  // the canvas and the control is added there; anywhere else and the palette
  // comes back. Not in the Navigate-embedded palette, which has no canvas
  // under it, and not for the pedal tile, which is a multi-step flow.
  const [carry, setCarry] = createSignal<{ name: string; x: number; y: number } | null>(null);
  let suppressClick = false;
  function onTilePointerDown(entry: PaletteEntry, e: PointerEvent): void {
    if (props.embedded || entry.id === 'guitarFxPreset' || entry.id === 'abletonControl' || entry.disabledReason?.()) return;
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    const sx = e.clientX, sy = e.clientY;
    const mouse = e.pointerType === 'mouse';
    let started = false;
    let hold: ReturnType<typeof setTimeout> | undefined;
    const begin = (x: number, y: number) => { started = true; setCarry({ name: entry.name, x, y }); };
    if (!mouse) hold = setTimeout(() => begin(sx, sy), 350);
    // A carried touch must not scroll anything underneath it.
    const noScroll = (te: TouchEvent) => { if (started) te.preventDefault(); };
    const move = (m: PointerEvent) => {
      if (m.pointerId !== e.pointerId) return;
      if (!started) {
        if (Math.hypot(m.clientX - sx, m.clientY - sy) <= (mouse ? 5 : 10)) return;
        if (mouse) begin(m.clientX, m.clientY);
        else { done(); return; } // moved before the hold: a scroll
      }
      setCarry({ name: entry.name, x: m.clientX, y: m.clientY });
    };
    const up = (u: PointerEvent) => {
      if (u.pointerId !== e.pointerId) return;
      const wasCarrying = started;
      done();
      if (!wasCarrying) return;
      suppressClick = true;
      setTimeout(() => { suppressClick = false; }, 0);
      dropAt(entry, u.clientX, u.clientY);
    };
    const cancel = (c: PointerEvent) => { if (c.pointerId === e.pointerId) { done(); setCarry(null); } };
    function done(): void {
      if (hold !== undefined) clearTimeout(hold);
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      window.removeEventListener('pointercancel', cancel);
      window.removeEventListener('touchmove', noScroll);
    }
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
    window.addEventListener('pointercancel', cancel);
    window.addEventListener('touchmove', noScroll, { passive: false });
  }
  function dropAt(entry: PaletteEntry, clientX: number, clientY: number): void {
    setCarry(null);
    const canvas = document.querySelector('.scaled-canvas-inner') as HTMLElement | null;
    if (!canvas || canvas.clientWidth === 0) return;
    const r = canvas.getBoundingClientRect();
    if (clientX < r.left || clientX > r.right || clientY < r.top || clientY > r.bottom) return;
    const scale = r.width / canvas.clientWidth;
    setSpawnPoint({ x: (clientX - r.left) / scale, y: (clientY - r.top) / scale });
    try { addAndAnnounce(entry.add); } finally { setSpawnPoint(null); }
    closeAll();
  }
  onCleanup(() => setCarry(null));

  function pickPedal(pedal: GuitarPedal): void {
    if (pedal.type === 'helix') {
      addAndAnnounce(() => addPresetChangeForPedal(pedal));
      closeAll();
      return;
    }
    // Custom: no compound protocol to bind directly to, so name/pick a
    // preset next rather than dropping an unconfigured button.
    setPickedPedal(pedal);
    setPedalStep('preset');
  }

  function useExistingPreset(pc: number, name: string): void {
    const pedal = pickedPedal();
    if (!pedal) return;
    addAndAnnounce(() => addGuitarFxButton(pedal, pc, name));
    closeAll();
  }

  function addNewPreset(): void {
    const pedal = pickedPedal();
    if (!pedal) return;
    const pc = Number(newPresetPc());
    const name = newPresetName().trim();
    if (!Number.isInteger(pc) || pc < 0 || pc > 127 || !name) return;
    updatePedal(pedal.id, { presetNames: { ...(pedal.presetNames ?? {}), [pc]: name } });
    addAndAnnounce(() => addGuitarFxButton(pedal, pc, name));
    closeAll();
  }

  const namedPresets = () => {
    const pedal = pickedPedal();
    if (!pedal) return [];
    return Object.entries(pedal.presetNames ?? {})
      .map(([pc, name]) => ({ pc: Number(pc), name }))
      .sort((a, b) => a.pc - b.pc);
  };

  const canAddNewPreset = () => {
    const pc = Number(newPresetPc());
    return newPresetName().trim().length > 0 && Number.isInteger(pc) && pc >= 0 && pc <= 127;
  };

  // The panel's content, shared by the overlay and Navigate's pane (2026-10-05).
  const panel = () => (<>
        <div class="palette-head">
          <div class="palette-title">
            {abletonPick() ? 'ABLETON CONTROL'
              : pedalStep() === 'pedal' ? 'PICK A PEDAL'
              : pedalStep() === 'preset' ? `NAME A PRESET · ${pickedPedal()?.name}`
                : 'ADD ITEM'}
          </div>
          <button
            type="button"
            class="palette-close"
            onClick={() => {
              if (abletonPick()) { setAbletonPick(false); return; }
              if (pedalStep() === 'preset') { setPedalStep('pedal'); setPickedPedal(null); return; }
              if (pedalStep() === 'pedal') { resetPedalFlow(); return; }
              closeAll();
            }}
            aria-label={pedalStep() || abletonPick() ? 'Back' : 'Close'}
            title={pedalStep() || abletonPick() ? 'Back' : 'Close (Esc)'}
          >
            {pedalStep() || abletonPick() ? '‹' : '✕'}
          </button>
        </div>

        <Show when={pedalStep() === 'pedal'}>
          <div class="backup-picker-body">
            <p class="backup-picker-sub">
              Helix binds straight to a preset tile. Other pedals pick or name a preset next.
            </p>
            <ul class="backup-picker-list">
              <For each={activePedals()}>
                {(pedal) => (
                  <li>
                    <button type="button" class="backup-picker-row" onClick={() => pickPedal(pedal)}>
                      <span class="backup-picker-row-stamp">{pedal.name}</span>
                      <span class="backup-picker-row-meta">
                        {pedalModel(pedal.modelId)?.model ?? (pedal.type === 'helix' ? 'Helix' : 'Generic')}
                        {' · '}
                        {pedal.port ?? 'no port set'}
                      </span>
                    </button>
                  </li>
                )}
              </For>
            </ul>
          </div>
        </Show>

        <Show when={pedalStep() === 'preset'}>
          <div class="backup-picker-body">
            <Show
              when={namedPresets().length > 0}
              fallback={(
                <div class="backup-picker-sub">
                  This pedal has no named presets yet — type the first one below.
                </div>
              )}
            >
              <div class="backup-picker-sub">Named presets</div>
              <ul class="backup-picker-list">
                <For each={namedPresets()}>
                  {(p) => (
                    <li>
                      <button type="button" class="backup-picker-row" onClick={() => useExistingPreset(p.pc, p.name)}>
                        <span class="backup-picker-row-stamp">{p.name}</span>
                        <span class="backup-picker-row-meta">PC {p.pc}</span>
                      </button>
                    </li>
                  )}
                </For>
              </ul>
            </Show>

            <p class="backup-picker-sub">Name a new one - typed once, reused from here on.</p>
            <div class="sk-urow">
              <input
                type="text"
                class="guitar-pedal-name"
                placeholder="Preset name"
                value={newPresetName()}
                onInput={(e) => setNewPresetName(e.currentTarget.value)}
              />
              <label class="sk-num">
                <span class="sk-num-key">PC</span>
                <input
                  type="number"
                  min="0"
                  max="127"
                  placeholder="0"
                  value={newPresetPc()}
                  onInput={(e) => setNewPresetPc(e.currentTarget.value)}
                />
              </label>
              <button type="button" class="sk-b" disabled={!canAddNewPreset()} onClick={addNewPreset}>
                Add &amp; Use
              </button>
            </div>
          </div>
        </Show>

        {/* Ableton Control (DEC-291): what to control, then the form. Kit
            rows (DEC-255). Its own Back, because Navigate's embedded palette
            hides the head that carries the other one. */}
        <Show when={abletonPick()}>
          <div class="ableton-pick" data-ableton-pick-list>
            <div class="nv-row ableton-pick-head">
              <button type="button" class="nv-b nv-b-q nv-b-sm ableton-pick-back" onClick={() => setAbletonPick(false)}>‹ Back</button>
              <span class="ableton-pick-sub">One button sets one value. A full row has every value, lit from Live.</span>
            </div>
            <div class="nav-well">
              <For each={ABLETON_CONTROLS}>
                {(c) => (
                  <div class="nav-lr ableton-pick-row" data-ableton-pick={c.id}>
                    <span class="nav-lr-main">
                      <b>{c.label}</b>
                      <small>{c.description}</small>
                    </span>
                    <span class="nav-lr-trail">
                      <button type="button" class="nv-b nv-b-sm" data-form="button"
                        onClick={() => addAbleton(c, 'button')}>One button</button>
                      <Show when={c.row !== null} fallback={<button type="button" class="nv-b nv-b-sm nv-b-pri ableton-pick-norow" tabindex="-1" aria-hidden="true" disabled>Full row</button>}>
                        <button type="button" class="nv-b nv-b-sm nv-b-pri" data-form="row"
                          onClick={() => addAbleton(c, 'row')}>Full row</button>
                      </Show>
                    </span>
                  </div>
                )}
              </For>
            </div>
          </div>
        </Show>

        <Show when={!pedalStep() && !abletonPick()}>
          <div class="palette-grid">
            <For each={ENTRIES}>
              {(entry) => {
                const reason = () => entry.disabledReason?.();
                return (
                  <button
                    type="button"
                    class="palette-tile"
                    data-disabled={reason() ? 'true' : 'false'}
                    disabled={!!reason()}
                    onClick={() => { if (!suppressClick) pick(entry); }}
                    onPointerDown={(e) => onTilePointerDown(entry, e)}
                    title={reason() ?? entry.description}
                  >
                    <div class="palette-thumb">
                      <entry.Thumb />
                    </div>
                    <div class="palette-tile-name">{entry.name}</div>
                    <div class="palette-tile-desc">{reason() ?? entry.description}</div>
                  </button>
                );
              }}
            </For>
          </div>
        </Show>
  </>);
  if (props.embedded) {
    return <div class="palette-panel nav-pane-body" data-embedded="true" role="region" aria-label="Add a control">{panel()}</div>;
  }

  return (
    <div
      class="palette-overlay"
      data-carrying={carry() ? 'true' : undefined}
      onClick={(e) => {
        if (e.target === e.currentTarget) closeAll();
      }}
      role="dialog"
      aria-label="Add layout item"
    >
      <div class="palette-panel">
        {panel()}
      </div>
      <Show when={carry()}>
        {(c) => (
          <Portal>
            <div class="palette-carry" style={{ left: `${c().x}px`, top: `${c().y}px` }}>{c().name}</div>
          </Portal>
        )}
      </Show>
    </div>
  );
}

// ─── Thumbnails — pure SVG, sized to fit the .palette-thumb 180×100 frame.
// Each evokes the real control without trying to be a perfect snapshot.

function ButtonThumb() {
  return (
    <svg viewBox="0 0 180 100" preserveAspectRatio="xMidYMid meet" aria-hidden="true">
      <circle cx="90" cy="50" r="32" fill="var(--color-idle)" opacity="0.18" />
      <circle cx="90" cy="50" r="32" fill="none" stroke="var(--color-idle)" stroke-width="2" />
      <text x="90" y="55" text-anchor="middle"
        font-family="var(--font-mono)" font-size="14" font-weight="700"
        fill="var(--color-idle)">ON</text>
    </svg>
  );
}

function ButtonGridThumb() {
  const cells = [
    { x: 8,   color: 'var(--color-idle)' },
    { x: 42,  color: 'var(--color-incoming)' },
    { x: 76,  color: 'var(--color-armed)' },
    { x: 110, color: 'var(--color-ok)' },
    { x: 144, color: 'var(--color-fg-dim)' },
  ];
  return (
    <svg viewBox="0 0 180 100" preserveAspectRatio="xMidYMid meet" aria-hidden="true">
      {cells.map((c, i) => (
        <g>
          <rect x={c.x} y="30" width="30" height="40" rx="3"
            fill={i === 1 ? c.color : 'none'}
            stroke={c.color} stroke-width="1.5"
            opacity={i === 1 ? '0.85' : '0.6'} />
          <text x={c.x + 15} y="55" text-anchor="middle"
            font-family="var(--font-mono)" font-size="10" font-weight="700"
            fill={i === 1 ? 'var(--color-canvas)' : c.color}>
            {i + 1}
          </text>
        </g>
      ))}
    </svg>
  );
}

function LfoThumb() {
  // One full sine wave with a playhead dot.
  const points: string[] = [];
  for (let i = 0; i <= 40; i++) {
    const x = 8 + (i / 40) * 164;
    const y = 50 - Math.sin((i / 40) * Math.PI * 2) * 26;
    points.push(`${x.toFixed(1)},${y.toFixed(1)}`);
  }
  return (
    <svg viewBox="0 0 180 100" preserveAspectRatio="xMidYMid meet" aria-hidden="true">
      <line x1="8" y1="50" x2="172" y2="50"
        stroke="var(--color-fg-dim)" stroke-width="1" stroke-dasharray="2 3" opacity="0.5" />
      <polyline points={points.join(' ')}
        fill="none" stroke="var(--color-incoming)" stroke-width="2" />
      <circle cx="98" cy={(50 - Math.sin((25 / 40) * Math.PI * 2) * 26).toFixed(1)}
        r="3.5" fill="var(--color-incoming)" />
    </svg>
  );
}

function SequencerThumb() {
  // 4 lanes × 12 steps, a few lit to evoke a kick pattern.
  const lanes = 4;
  const steps = 12;
  const lit: Record<string, boolean> = {
    '0,0': true, '0,4': true, '0,8': true,
    '1,4': true, '1,12': false,
    '2,2': true, '2,6': true, '2,10': true,
    '3,3': true,
  };
  const cellW = 12;
  const cellH = 14;
  const totalW = steps * cellW;
  const startX = (180 - totalW) / 2;
  const startY = (100 - lanes * cellH) / 2;
  const rects: JSX.Element[] = [];
  for (let r = 0; r < lanes; r++) {
    for (let s = 0; s < steps; s++) {
      const on = lit[`${r},${s}`];
      rects.push(
        <rect
          x={startX + s * cellW + 1}
          y={startY + r * cellH + 1}
          width={cellW - 2}
          height={cellH - 2}
          rx="1.5"
          fill={on ? 'var(--color-idle)' : 'none'}
          stroke="var(--color-fg-dim)"
          stroke-width="1"
          opacity={on ? 0.9 : 0.45}
        />,
      );
    }
  }
  return (
    <svg viewBox="0 0 180 100" preserveAspectRatio="xMidYMid meet" aria-hidden="true">
      {rects}
    </svg>
  );
}

function MacroThumb() {
  // Two stacked shape lanes — a riser (exp up) and a drop (exp down).
  // Suggests "macros are multi-lane MIDI sweeps."
  const riserPts: string[] = [];
  const dropPts: string[] = [];
  for (let i = 0; i <= 40; i++) {
    const t = i / 40;
    const x = 8 + t * 164;
    const yRiser = 38 - Math.pow(t, 2) * 22;
    const yDrop = 62 + (1 - Math.pow(1 - t, 2)) * 22;
    riserPts.push(`${x.toFixed(1)},${yRiser.toFixed(1)}`);
    dropPts.push(`${x.toFixed(1)},${yDrop.toFixed(1)}`);
  }
  return (
    <svg viewBox="0 0 180 100" preserveAspectRatio="xMidYMid meet" aria-hidden="true">
      <line x1="8" y1="50" x2="172" y2="50"
        stroke="var(--color-fg-dim)" stroke-width="1" stroke-dasharray="2 3" opacity="0.4" />
      <polyline points={riserPts.join(' ')}
        fill="none" stroke="var(--color-incoming)" stroke-width="2" />
      <polyline points={dropPts.join(' ')}
        fill="none" stroke="var(--color-armed)" stroke-width="2" />
      <text x="14" y="22" font-family="var(--font-mono)" font-size="8"
        fill="var(--color-incoming)" letter-spacing="1">L1</text>
      <text x="14" y="92" font-family="var(--font-mono)" font-size="8"
        fill="var(--color-armed)" letter-spacing="1">L2</text>
    </svg>
  );
}

function PresetChangeThumb() {
  // A preset tile: the name large, the slot notation small beneath it —
  // exactly what the item renders, so the palette previews the real thing
  // rather than an abstract glyph.
  return (
    <svg viewBox="0 0 180 100" preserveAspectRatio="xMidYMid meet" aria-hidden="true">
      <rect
        x="18" y="20" width="144" height="60" rx="8"
        fill="none" stroke="var(--color-ok)" stroke-width="1.5" opacity="0.85"
      />
      <text
        x="90" y="49" text-anchor="middle"
        font-size="17" fill="var(--color-ok)" opacity="0.95"
      >
        Surf
      </text>
      <text
        x="90" y="67" text-anchor="middle"
        font-size="10" fill="var(--color-ok)" opacity="0.55"
      >
        SL3 · 02B
      </text>
    </svg>
  );
}

function SnapshotChangeThumb() {
  // A ROW of snapshot tiles with one lit — the canonical layout, and the
  // visual difference from the preset thumb (a single wide named tile).
  // Says "these come in banks of eight" without a word of copy.
  return (
    <svg viewBox="0 0 180 100" preserveAspectRatio="xMidYMid meet" aria-hidden="true">
      {[0, 1, 2, 3].map((i) => {
        const lit = i === 1;
        return (
          <g>
            <rect
              x={16 + i * 38} y="32" width="32" height="36" rx="6"
              fill={lit ? 'var(--color-idle)' : 'none'}
              fill-opacity={lit ? 0.18 : 0}
              stroke="var(--color-idle)" stroke-width="1.5"
              opacity={lit ? 0.95 : 0.45}
            />
            <text
              x={32 + i * 38} y="55" text-anchor="middle"
              font-size="14" fill="var(--color-idle)"
              opacity={lit ? 0.95 : 0.5}
            >
              {i + 1}
            </text>
          </g>
        );
      })}
    </svg>
  );
}

/* ── Lighting thumbs ──────────────────────────────────────────────────────
   Each one shows the CONTROL's idea, not a light bulb. A palette of four
   generic bulb icons would make the operator read four descriptions to tell
   them apart; a swatch row, a fader, a burst and a struck-through tile are
   each recognisable at thumbnail size without reading anything.

   Colour tokens follow the spawners' defaults so the tile previews the tile
   you get: cyan for Colour/Level, magenta for FX, amber for Blackout. */

function LightColorThumb() {
  // Three swatches in real palette colours, not token colours - this is the
  // one tile where actual hue IS the subject, so borrowing --color-idle for
  // all three would hide what the control does.
  const swatches = ['#ff0000', '#00ff00', '#0033ff'];
  return (
    <svg viewBox="0 0 180 100" preserveAspectRatio="xMidYMid meet" aria-hidden="true">
      {swatches.map((hex, i) => (
        <rect
          x={30 + i * 42} y="28" width="36" height="36" rx="6"
          fill={hex} fill-opacity="0.85"
          stroke="var(--color-idle)" stroke-width="1" opacity="0.95"
        />
      ))}
      <text x="90" y="86" text-anchor="middle" font-size="11" fill="var(--color-idle)" opacity="0.6">
        COLOUR
      </text>
    </svg>
  );
}

function LightLevelThumb() {
  return (
    <svg viewBox="0 0 180 100" preserveAspectRatio="xMidYMid meet" aria-hidden="true">
      <rect
        x="82" y="16" width="16" height="56" rx="8"
        fill="var(--color-idle)" fill-opacity="0.12"
        stroke="var(--color-idle)" stroke-width="1.5" opacity="0.9"
      />
      <rect x="82" y="44" width="16" height="28" rx="8" fill="var(--color-idle)" opacity="0.5" />
      <rect x="74" y="40" width="32" height="8" rx="4" fill="var(--color-idle)" opacity="0.95" />
      <text x="90" y="86" text-anchor="middle" font-size="11" fill="var(--color-idle)" opacity="0.6">
        LEVEL
      </text>
    </svg>
  );
}

function LightFxThumb() {
  // A burst: eight rays out of a centre. Reads as "something is happening"
  // at 40px, which is the whole point of an effect control.
  const rays = [0, 45, 90, 135, 180, 225, 270, 315];
  return (
    <svg viewBox="0 0 180 100" preserveAspectRatio="xMidYMid meet" aria-hidden="true">
      {rays.map((deg) => {
        const rad = (deg * Math.PI) / 180;
        return (
          <line
            x1={90 + Math.cos(rad) * 9} y1={44 + Math.sin(rad) * 9}
            x2={90 + Math.cos(rad) * 24} y2={44 + Math.sin(rad) * 24}
            stroke="var(--color-incoming)" stroke-width="2.5" stroke-linecap="round" opacity="0.85"
          />
        );
      })}
      <circle cx="90" cy="44" r="6" fill="var(--color-incoming)" opacity="0.9" />
      <text x="90" y="86" text-anchor="middle" font-size="11" fill="var(--color-incoming)" opacity="0.6">
        FX
      </text>
    </svg>
  );
}

function HueSceneThumb() {
  // A ceiling of spots holding one scene: a few soft colours side by side, the way the Hue app draws a scene, and the
  // same strip the button wears along its face.
  const spots = ['#ff7a3d', '#ff4f8b', '#b45cff', '#5c7dff', '#ffb35c'];
  return (
    <svg viewBox="0 0 180 100" preserveAspectRatio="xMidYMid meet" aria-hidden="true">
      <path d="M 30 26 L 150 26" stroke="var(--color-incoming)" stroke-width="1.5" stroke-linecap="round" opacity="0.35" />
      {spots.map((c, i) => (
        <g>
          <path d={`M ${42 + i * 24} 26 L ${42 + i * 24} 34`} stroke="var(--color-incoming)" stroke-width="1" opacity="0.4" />
          <circle cx={42 + i * 24} cy="44" r="9" fill={c} fill-opacity="0.85" />
          <circle cx={42 + i * 24} cy="44" r="15" fill={c} fill-opacity="0.18" />
        </g>
      ))}
      <rect x="40" y="64" width="100" height="5" rx="2.5" fill="url(#hue-thumb-g)" opacity="0.9" />
      <defs>
        <linearGradient id="hue-thumb-g" x1="0" x2="1" y1="0" y2="0">
          {spots.map((c, i) => <stop offset={`${(i / (spots.length - 1)) * 100}%`} stop-color={c} />)}
        </linearGradient>
      </defs>
      <text x="90" y="88" text-anchor="middle" font-size="11" fill="var(--color-incoming)" opacity="0.6">
        SCENE
      </text>
    </svg>
  );
}

function LightPresetThumb() {
  // A chase caught mid-run: a row of lights with a comet travelling along
  // it, bright at the head and fading behind. Motion is what separates a
  // preset from a Light Colour tile at 40px.
  const lights = [0.12, 0.2, 0.35, 0.6, 0.95];
  return (
    <svg viewBox="0 0 180 100" preserveAspectRatio="xMidYMid meet" aria-hidden="true">
      {lights.map((o, i) => (
        <circle
          cx={42 + i * 24} cy="44" r="9"
          fill="var(--color-incoming)" fill-opacity={o}
          stroke="var(--color-incoming)" stroke-width="1" opacity="0.9"
        />
      ))}
      <path d="M 40 60 L 146 60" stroke="var(--color-incoming)" stroke-width="1.5" stroke-linecap="round" opacity="0.35" />
      <path d="M 138 56 L 146 60 L 138 64" fill="none" stroke="var(--color-incoming)" stroke-width="1.5" stroke-linecap="round" opacity="0.6" />
      <text x="90" y="86" text-anchor="middle" font-size="11" fill="var(--color-incoming)" opacity="0.6">
        PRESET
      </text>
    </svg>
  );
}

function BlackoutThumb() {
  return (
    <svg viewBox="0 0 180 100" preserveAspectRatio="xMidYMid meet" aria-hidden="true">
      <rect
        x="40" y="22" width="100" height="44" rx="8"
        fill="var(--color-armed)" fill-opacity="0.10"
        stroke="var(--color-armed)" stroke-width="1.5" opacity="0.9"
      />
      <line
        x1="48" y1="62" x2="132" y2="26"
        stroke="var(--color-armed)" stroke-width="3" stroke-linecap="round" opacity="0.95"
      />
      <text x="90" y="86" text-anchor="middle" font-size="11" fill="var(--color-armed)" opacity="0.6">
        BLACKOUT
      </text>
    </svg>
  );
}

/* ── OPVisual thumbs ──────────────────────────────────────────────────────
   The Grid thumb IS the grid: a gutter and a block of cells, one of them
   lit. The Trigger thumb is a single tile carrying a cell address, which is
   exactly what the operator will see on the placed button. */

function OpVisualGridThumb() {
  const cells: Array<{ x: number; y: number; lit: boolean }> = [];
  for (let r = 0; r < 4; r++) {
    for (let c = 0; c < 6; c++) {
      cells.push({ x: 48 + c * 19, y: 18 + r * 15, lit: r === 1 && c === 2 });
    }
  }
  return (
    <svg viewBox="0 0 180 100" preserveAspectRatio="xMidYMid meet" aria-hidden="true">
      {[0, 1, 2, 3].map((r) => (
        <rect x="26" y={20 + r * 15} width="16" height="9" rx="2"
          fill="var(--color-idle)" fill-opacity="0.18" stroke="var(--color-idle)" stroke-width="0.8" opacity="0.7" />
      ))}
      {cells.map((c) => (
        <rect x={c.x} y={c.y} width="16" height="12" rx="2"
          fill="var(--color-idle)" fill-opacity={c.lit ? '0.85' : '0.10'}
          stroke="var(--color-idle)" stroke-width="1" opacity="0.9" />
      ))}
      <text x="90" y="92" text-anchor="middle" font-size="11" fill="var(--color-idle)" opacity="0.6">
        VISUALS
      </text>
    </svg>
  );
}

function AudioPadThumb() {
  return (
    <svg viewBox="0 0 180 100" preserveAspectRatio="xMidYMid meet" aria-hidden="true">
      <rect x="55" y="16" width="70" height="62" rx="8" fill="var(--color-idle)" fill-opacity="0.12" stroke="var(--color-idle)" stroke-width="1.5" opacity="0.9" />
      <path d="M 68 47 l 6 -10 l 6 20 l 6 -26 l 6 30 l 6 -18 l 6 8 l 6 -4" fill="none" stroke="var(--color-idle)" stroke-width="2" stroke-linejoin="round" opacity="0.9" />
      <rect x="62" y="70" width="34" height="3" rx="1.5" fill="var(--color-idle)" opacity="0.8" />
      <text x="90" y="94" text-anchor="middle" font-size="11" fill="var(--color-idle)" opacity="0.6">AUDIO</text>
    </svg>
  );
}

function AudioPadsThumb() {
  const cells = [0, 1, 2, 3, 4, 5, 6, 7];
  return (
    <svg viewBox="0 0 180 100" preserveAspectRatio="xMidYMid meet" aria-hidden="true">
      {cells.map((i) => (
        <rect
          x={30 + (i % 4) * 31} y={18 + Math.floor(i / 4) * 34} width="27" height="30" rx="5"
          fill="var(--color-idle)" fill-opacity={i === 1 || i === 6 ? '0.55' : '0.12'}
          stroke="var(--color-idle)" stroke-width="1.2" opacity="0.9"
        />
      ))}
      <text x="90" y="96" text-anchor="middle" font-size="11" fill="var(--color-idle)" opacity="0.6">SOUNDBOARD</text>
    </svg>
  );
}

function OpVisualTriggerThumb() {
  return (
    <svg viewBox="0 0 180 100" preserveAspectRatio="xMidYMid meet" aria-hidden="true">
      <rect
        x="50" y="20" width="80" height="48" rx="8"
        fill="var(--color-idle)" fill-opacity="0.12"
        stroke="var(--color-idle)" stroke-width="1.5" opacity="0.9"
      />
      <text x="90" y="50" text-anchor="middle" font-size="16" font-weight="700" fill="var(--color-idle)" opacity="0.95">
        L1-3
      </text>
      <text x="90" y="88" text-anchor="middle" font-size="11" fill="var(--color-idle)" opacity="0.6">
        VISUALS
      </text>
    </svg>
  );
}

function RigStateButtonThumb() {
  // A single wide tile (like PresetChange's) but with three small dots
  // inside it - the "chord" visual: one press, several targets recalled
  // together, distinct from SnapshotChange's row-of-eight and from a plain
  // Button's single dot.
  return (
    <svg viewBox="0 0 180 100" preserveAspectRatio="xMidYMid meet" aria-hidden="true">
      <rect
        x="24" y="28" width="132" height="44" rx="8"
        fill="var(--color-armed)" fill-opacity="0.14"
        stroke="var(--color-armed)" stroke-width="1.5" opacity="0.9"
      />
      {[0, 1, 2].map((i) => (
        <circle cx={78 + i * 14} cy="50" r="4" fill="var(--color-armed)" opacity="0.85" />
      ))}
      <text
        x="90" y="86" text-anchor="middle"
        font-size="11" fill="var(--color-armed)" opacity="0.6"
      >
        RIG STATE
      </text>
    </svg>
  );
}

function StatusLedThumb() {
  // A lit LED (bigger, glowing) with a dim companion beside it — visual
  // shorthand for "these light up when their bound signal fires."
  return (
    <svg viewBox="0 0 180 100" preserveAspectRatio="xMidYMid meet" aria-hidden="true">
      <defs>
        <radialGradient id="led-glow-thumb" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stop-color="var(--color-ok)" stop-opacity="0.9" />
          <stop offset="70%" stop-color="var(--color-ok)" stop-opacity="0.25" />
          <stop offset="100%" stop-color="var(--color-ok)" stop-opacity="0" />
        </radialGradient>
      </defs>
      {/* Lit LED */}
      <circle cx="70" cy="50" r="34" fill="url(#led-glow-thumb)" />
      <circle cx="70" cy="50" r="14" fill="var(--color-ok)" />
      <circle cx="70" cy="50" r="14" fill="none" stroke="var(--color-ok)" stroke-width="1.5" opacity="0.9" />
      {/* Dim LED */}
      <circle cx="130" cy="50" r="14" fill="none" stroke="var(--color-ok)" stroke-width="1.5" opacity="0.35" />
    </svg>
  );
}

function StreamDeckThumb() {
  // Big "key tile" with a press indicator.
  return (
    <svg viewBox="0 0 180 100" preserveAspectRatio="xMidYMid meet" aria-hidden="true">
      <rect x="62" y="16" width="56" height="56" rx="8"
        fill="var(--color-armed)" opacity="0.18" />
      <rect x="62" y="16" width="56" height="56" rx="8"
        fill="none" stroke="var(--color-armed)" stroke-width="2" />
      <text x="90" y="44" text-anchor="middle"
        font-family="var(--font-mono)" font-size="11" font-weight="700"
        fill="var(--color-armed)">K1</text>
      <text x="90" y="60" text-anchor="middle"
        font-family="var(--font-mono)" font-size="8"
        fill="var(--color-armed)" opacity="0.8">SD</text>
      <text x="90" y="88" text-anchor="middle"
        font-family="var(--font-mono)" font-size="8"
        fill="var(--color-fg-dim)" letter-spacing="1.5">PRESS</text>
    </svg>
  );
}

function RadialThumb() {
  return (
    <svg viewBox="0 0 48 48" class="palette-thumb-svg" aria-hidden="true">
      <path d="M12 34 A 16 16 0 1 1 36 34" fill="none" stroke="currentColor" stroke-width="3" opacity="0.35" stroke-linecap="round" />
      <path d="M12 34 A 16 16 0 0 1 14 14" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" />
      <circle cx="24" cy="24" r="10" fill="none" stroke="currentColor" stroke-width="2" />
      <line x1="24" y1="24" x2="17" y2="17" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" />
    </svg>
  );
}

function LabelThumb() {
  return (
    <svg viewBox="0 0 48 48" class="palette-thumb-svg" aria-hidden="true">
      <line x1="10" y1="18" x2="38" y2="18" stroke="currentColor" stroke-width="3" stroke-linecap="round" />
      <line x1="10" y1="25" x2="30" y2="25" stroke="currentColor" stroke-width="3" stroke-linecap="round" opacity="0.6" />
      <line x1="10" y1="32" x2="34" y2="32" stroke="currentColor" stroke-width="3" stroke-linecap="round" opacity="0.35" />
    </svg>
  );
}

function AbletonControlThumb() {
  // A tempo/quantize value on a button face. Reads as "a control that SETS a
  // session value", which is what separates it from DisplayThumb's screen
  // (which only shows one). The small chevron marks it as a send, not a
  // readout.
  return (
    <svg viewBox="0 0 48 48" class="palette-thumb-svg" aria-hidden="true">
      <rect x="6" y="11" width="36" height="26" rx="5" fill="none" stroke="currentColor" stroke-width="2" />
      <line x1="12" y1="20" x2="26" y2="20" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" />
      <line x1="12" y1="27" x2="21" y2="27" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" opacity="0.55" />
      <path d="M31 20 l5 4 -5 4" fill="none" stroke="currentColor" stroke-width="2.5"
        stroke-linecap="round" stroke-linejoin="round" />
    </svg>
  );
}

function ObsSceneThumb() {
  // A camera-ish frame with a live dot, over a second frame peeking behind
  // it: "one of several scenes, and this is the one on air". The dot is what
  // separates it from AbletonControlThumb's send chevron - this control has a
  // state, and the state is the feature.
  return (
    <svg viewBox="0 0 48 48" class="palette-thumb-svg" aria-hidden="true">
      <rect x="12" y="9" width="30" height="20" rx="3" fill="none" stroke="currentColor"
        stroke-width="2" opacity="0.35" />
      <rect x="6" y="17" width="30" height="21" rx="3" fill="none" stroke="currentColor" stroke-width="2" />
      <circle cx="13" cy="24" r="2.6" fill="currentColor" />
      <line x1="19" y1="24" x2="30" y2="24" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" />
      <line x1="19" y1="31" x2="26" y2="31" stroke="currentColor" stroke-width="2.5"
        stroke-linecap="round" opacity="0.5" />
    </svg>
  );
}

function RunProgramThumb() {
  // An app window (title bar with a dot) with a play triangle inside it:
  // "start this program". The dot on the title bar echoes the running light
  // the button carries, the way ObsSceneThumb's live dot does.
  return (
    <svg viewBox="0 0 48 48" class="palette-thumb-svg" aria-hidden="true">
      <rect x="6" y="9" width="36" height="30" rx="3" fill="none" stroke="currentColor" stroke-width="2" />
      <line x1="6" y1="16" x2="42" y2="16" stroke="currentColor" stroke-width="2" opacity="0.5" />
      <circle cx="11" cy="12.5" r="1.6" fill="currentColor" />
      <path d="M20 21 L31 27.5 L20 34 Z" fill="currentColor" />
    </svg>
  );
}

function AbletonDeviceThumb() {
  // A device slot in a chain - a rounded rack unit with a power pip lit at
  // its left, the way Live draws a device's on/off. The two ghosted slivers
  // either side say "one device among several in the chain", which is what
  // the name filter is choosing between.
  return (
    <svg viewBox="0 0 48 48" class="palette-thumb-svg" aria-hidden="true">
      <rect x="3" y="14" width="5" height="20" rx="2" fill="none" stroke="currentColor"
        stroke-width="2" opacity="0.3" />
      <rect x="40" y="14" width="5" height="20" rx="2" fill="none" stroke="currentColor"
        stroke-width="2" opacity="0.3" />
      <rect x="12" y="12" width="24" height="24" rx="3" fill="none" stroke="currentColor"
        stroke-width="2" />
      <circle cx="18" cy="18" r="2.6" fill="currentColor" />
      <line x1="17" y1="26" x2="31" y2="26" stroke="currentColor" stroke-width="2.5"
        stroke-linecap="round" opacity="0.65" />
      <line x1="17" y1="31" x2="26" y2="31" stroke="currentColor" stroke-width="2.5"
        stroke-linecap="round" opacity="0.35" />
    </svg>
  );
}

function DisplayThumb() {
  // A small LCD bezel with three lines of "text" inside - visual shorthand
  // for the 3-line glance readout. Deliberately close to LabelThumb's own
  // three-line mark, framed to read as a screen rather than a heading.
  return (
    <svg viewBox="0 0 48 48" class="palette-thumb-svg" aria-hidden="true">
      <rect x="6" y="10" width="36" height="28" rx="3" fill="none" stroke="currentColor" stroke-width="2" />
      <line x1="11" y1="18" x2="37" y2="18" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" />
      <line x1="11" y1="24" x2="30" y2="24" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" opacity="0.6" />
      <line x1="11" y1="30" x2="33" y2="30" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" opacity="0.35" />
    </svg>
  );
}

function FaderThumb() {
  // A vertical fader with the handle sitting at about 65% travel.
  return (
    <svg viewBox="0 0 180 100" preserveAspectRatio="xMidYMid meet" aria-hidden="true">
      {/* Track */}
      <rect x="86" y="12" width="8" height="76" rx="4"
        fill="var(--color-idle)" opacity="0.12" />
      <rect x="86" y="12" width="8" height="76" rx="4"
        fill="none" stroke="var(--color-idle)" stroke-width="1" opacity="0.5" />
      {/* Handle */}
      <rect x="72" y="34" width="36" height="14" rx="3"
        fill="var(--color-idle)" opacity="0.85" />
      <rect x="72" y="34" width="36" height="14" rx="3"
        fill="none" stroke="var(--color-idle)" stroke-width="1.5" />
      <line x1="78" y1="41" x2="102" y2="41"
        stroke="var(--color-canvas)" stroke-width="1.5" opacity="0.8" />
      <text x="90" y="99" text-anchor="middle"
        font-family="var(--font-mono)" font-size="8"
        fill="var(--color-idle)" letter-spacing="1.5">CC</text>
    </svg>
  );
}

function MixerThumb() {
  // Four mini channel strips at varying fader heights + a mute square each.
  const strips = [
    { x: 34, hy: 40 },
    { x: 74, hy: 26 },
    { x: 114, hy: 52 },
    { x: 154, hy: 34 },
  ];
  return (
    <svg viewBox="0 0 180 100" preserveAspectRatio="xMidYMid meet" aria-hidden="true">
      <For each={strips}>
        {(s) => (
          <>
            <rect x={s.x - 3} y="8" width="6" height="64" rx="3" fill="var(--color-idle)" opacity="0.12" />
            <line x1={s.x} y1="8" x2={s.x} y2="72" stroke="var(--color-idle)" stroke-width="1" opacity="0.4" />
            <rect x={s.x - 11} y={s.hy} width="22" height="8" rx="2" fill="var(--color-idle)" opacity="0.85" />
            <rect x={s.x - 7} y="80" width="14" height="11" rx="2" fill="none" stroke="var(--color-idle)" stroke-width="1" opacity="0.6" />
          </>
        )}
      </For>
    </svg>
  );
}

function XYPadThumb() {
  // A framed pad with a puck slightly off-center + faint crosshair.
  return (
    <svg viewBox="0 0 180 100" preserveAspectRatio="xMidYMid meet" aria-hidden="true">
      {/* Pad frame */}
      <rect x="52" y="8" width="76" height="76" rx="6"
        fill="var(--color-incoming)" opacity="0.08" />
      <rect x="52" y="8" width="76" height="76" rx="6"
        fill="none" stroke="var(--color-incoming)" stroke-width="1.5" />
      {/* Crosshair */}
      <line x1="52" y1="46" x2="128" y2="46"
        stroke="var(--color-incoming)" stroke-width="0.5" opacity="0.4"
        stroke-dasharray="2 2" />
      <line x1="90" y1="8" x2="90" y2="84"
        stroke="var(--color-incoming)" stroke-width="0.5" opacity="0.4"
        stroke-dasharray="2 2" />
      {/* Puck */}
      <circle cx="108" cy="30" r="9" fill="var(--color-incoming)" opacity="0.85" />
      <circle cx="108" cy="30" r="9" fill="none"
        stroke="var(--color-canvas)" stroke-width="1.5" />
      <text x="90" y="98" text-anchor="middle"
        font-family="var(--font-mono)" font-size="8"
        fill="var(--color-incoming)" letter-spacing="1.5">X · Y</text>
    </svg>
  );
}

function TimelineLauncherThumb() {
  // A big launch button on the left + three section buttons on the right,
  // suggesting the "whole + sections" mode.
  return (
    <svg viewBox="0 0 180 100" preserveAspectRatio="xMidYMid meet" aria-hidden="true">
      {/* Whole launcher tile */}
      <rect x="12" y="24" width="52" height="52" rx="6"
        fill="var(--color-ok)" opacity="0.2" />
      <rect x="12" y="24" width="52" height="52" rx="6"
        fill="none" stroke="var(--color-ok)" stroke-width="1.5" />
      <polygon points="30,38 30,62 50,50" fill="var(--color-ok)" opacity="0.9" />
      {/* Section pills */}
      <rect x="76"  y="26" width="92" height="16" rx="3"
        fill="var(--color-ok)" opacity="0.14" />
      <rect x="76"  y="26" width="92" height="16" rx="3"
        fill="none" stroke="var(--color-ok)" stroke-width="1" opacity="0.7" />
      <rect x="76"  y="46" width="92" height="16" rx="3"
        fill="var(--color-ok)" opacity="0.14" />
      <rect x="76"  y="46" width="92" height="16" rx="3"
        fill="none" stroke="var(--color-ok)" stroke-width="1" opacity="0.7" />
      <rect x="76"  y="66" width="92" height="16" rx="3"
        fill="var(--color-ok)" opacity="0.14" />
      <rect x="76"  y="66" width="92" height="16" rx="3"
        fill="none" stroke="var(--color-ok)" stroke-width="1" opacity="0.7" />
      <text x="122" y="37" text-anchor="middle"
        font-family="var(--font-mono)" font-size="8"
        fill="var(--color-ok)" opacity="0.9">VERSE</text>
      <text x="122" y="57" text-anchor="middle"
        font-family="var(--font-mono)" font-size="8"
        fill="var(--color-ok)" opacity="0.9">CHORUS</text>
      <text x="122" y="77" text-anchor="middle"
        font-family="var(--font-mono)" font-size="8"
        fill="var(--color-ok)" opacity="0.9">BRIDGE</text>
    </svg>
  );
}

function PianoThumb() {
  // 7 white keys + 5 black keys (one full octave) — the immediately-
  // recognisable piano visual. Middle white key is subtly highlighted
  // to suggest "keys are tappable."
  const whiteKeys = [10, 34, 58, 82, 106, 130, 154];
  const blackKeys = [26, 50, 98, 122, 146]; // gaps between white keys 1-2, 2-3, 4-5, 5-6, 6-7
  return (
    <svg viewBox="0 0 180 100" preserveAspectRatio="xMidYMid meet" aria-hidden="true">
      {/* White keys */}
      {whiteKeys.map((x, i) => (
        <rect
          x={x} y="16" width="22" height="70" rx="2"
          fill={i === 3 ? 'var(--color-idle)' : 'var(--color-fg)'}
          opacity={i === 3 ? '0.4' : '0.9'}
          stroke="var(--color-fg-dim)" stroke-width="0.5"
        />
      ))}
      {/* Black keys */}
      {blackKeys.map((x) => (
        <rect
          x={x} y="16" width="14" height="42" rx="1.5"
          fill="var(--color-canvas)"
          stroke="var(--color-fg-dim)" stroke-width="0.5"
        />
      ))}
      <text x="90" y="98" text-anchor="middle"
        font-family="var(--font-mono)" font-size="8"
        fill="var(--color-idle)" letter-spacing="1.5">PIANO</text>
    </svg>
  );
}

function TransposeThumb() {
  // A signed semitone row with 0 (home) filled — evokes "pick a transpose."
  const cells = [
    { x: 10,  label: '-2' },
    { x: 44,  label: '-1' },
    { x: 78,  label: '0', home: true },
    { x: 112, label: '+1' },
    { x: 146, label: '+2' },
  ];
  return (
    <svg viewBox="0 0 180 100" preserveAspectRatio="xMidYMid meet" aria-hidden="true">
      {cells.map((c) => (
        <g>
          <rect x={c.x} y="34" width="26" height="32" rx="3"
            fill={c.home ? 'var(--color-armed)' : 'none'}
            stroke="var(--color-armed)" stroke-width="1.5"
            opacity={c.home ? '0.85' : '0.55'} />
          <text x={c.x + 13} y="54" text-anchor="middle"
            font-family="var(--font-mono)" font-size="9" font-weight="700"
            fill={c.home ? 'var(--color-canvas)' : 'var(--color-armed)'}>
            {c.label}
          </text>
        </g>
      ))}
    </svg>
  );
}

function SceneCopyThumb() {
  // Three columns: source, destination, and the dashed CLEAR column at the
  // far edge. The thumb has to read as a source/destination PAIR plus a
  // separate destructive column, not as fifteen buttons - and the dashed
  // third column is the one visual cue that says "this one is different"
  // before the operator has read a single label.
  const rows = [24, 46, 68];
  return (
    <svg viewBox="0 0 180 100" preserveAspectRatio="xMidYMid meet" aria-hidden="true">
      {rows.map((y, i) => (
        <g>
          <rect x="14" y={y} width="38" height="16" rx="3"
            fill={i === 1 ? 'var(--color-incoming)' : 'none'}
            stroke="var(--color-incoming)" stroke-width="1.5"
            opacity={i === 1 ? '0.85' : '0.5'} />
          <rect x="72" y={y} width="38" height="16" rx="3"
            fill="none" stroke="var(--color-incoming)" stroke-width="1.5"
            opacity="0.5" />
          <rect x="130" y={y} width="38" height="16" rx="3"
            fill="none" stroke="var(--color-fg-dim)" stroke-width="1.5"
            stroke-dasharray="3 2" opacity="0.65" />
        </g>
      ))}
      <path d="M55 54 L69 54 M64 50 L69 54 L64 58"
        stroke="var(--color-incoming)" stroke-width="1.5" fill="none" opacity="0.9" />
    </svg>
  );
}

function CapoThumb() {
  // A capo-fret row with fret 1 filled — nods at the Eb-guitar case where
  // concert transpose 0 sits at capo +1.
  const cells = [
    { x: 10,  label: '0' },
    { x: 44,  label: '1', home: true },
    { x: 78,  label: '2' },
    { x: 112, label: '3' },
    { x: 146, label: '4' },
  ];
  return (
    <svg viewBox="0 0 180 100" preserveAspectRatio="xMidYMid meet" aria-hidden="true">
      {cells.map((c) => (
        <g>
          <rect x={c.x} y="34" width="26" height="32" rx="3"
            fill={c.home ? 'var(--color-ok)' : 'none'}
            stroke="var(--color-ok)" stroke-width="1.5"
            opacity={c.home ? '0.85' : '0.55'} />
          <text x={c.x + 13} y="54" text-anchor="middle"
            font-family="var(--font-mono)" font-size="9" font-weight="700"
            fill={c.home ? 'var(--color-canvas)' : 'var(--color-ok)'}>
            {c.label}
          </text>
        </g>
      ))}
    </svg>
  );
}

function WavesAutotuneThumb() {
  // Two key rows (Am / Em homes filled) + a MIN/MAJ/Chrom scale row -
  // evokes "pick a key and a scale for Waves autotune."
  const keyRows = [
    { y: 20, home: 'Am', cells: ['G', 'Am', 'A', 'B', 'C'] },
    { y: 46, home: 'Em', cells: ['D', 'Em', 'E', 'F', 'G'] },
  ];
  const cx = [10, 44, 78, 112, 146];
  // Must match `WavesAutotune.tsx`'s SCALES. It said ['MIN','MAJ','Chr'] until
  // 2026-09-02 - three modes, so the palette advertised ONE minor while the
  // item you actually get has had two since CP-118 added harmonic minor
  // (`SCALE_CC_VALUES.minHarm = 13`, measured on the plugin, never derived).
  // A preview that under-sells the item is how the operator concluded the
  // feature was missing. DOR joined 2026-09-11, and this line is the one that
  // gets forgotten - the item and the palette are two separate lists.
  const scales = ['CHR', 'MAJ', 'MIN', 'HARM', 'DOR'];
  return (
    <svg viewBox="0 0 180 100" preserveAspectRatio="xMidYMid meet" aria-hidden="true">
      {keyRows.map((row) =>
        row.cells.map((label, i) => {
          const home = label === row.home;
          return (
            <g>
              <rect x={cx[i]} y={row.y} width="26" height="22" rx="3"
                fill={home ? 'var(--color-incoming)' : 'none'}
                stroke="var(--color-incoming)" stroke-width="1.5"
                opacity={home ? '0.85' : '0.5'} />
              <text x={cx[i]! + 13} y={row.y + 15} text-anchor="middle"
                font-family="var(--font-mono)" font-size="8" font-weight="700"
                fill={home ? 'var(--color-canvas)' : 'var(--color-incoming)'}>
                {label}
              </text>
            </g>
          );
        }),
      )}
      {scales.map((label, i) => (
        <g>
          {/* FIVE cells in a 180-wide viewBox: pitch 33 / width 30 keeps the
              last one inside (x=142..172). Re-pitched twice now for the same
              reason - at 40/36 a fifth cell ran to x=206 and at the original
              55/49 a fourth ran to 224, both off the edge. The geometry has to
              move every time `scales` grows; it does not scale itself. */}
          <rect x={10 + i * 33} y="72" width="30" height="20" rx="3"
            fill="none" stroke="var(--color-incoming)" stroke-width="1.5" opacity="0.5" />
          <text x={10 + i * 33 + 15} y="86" text-anchor="middle"
            font-family="var(--font-mono)" font-size="8" font-weight="700"
            fill="var(--color-incoming)">
            {label}
          </text>
        </g>
      ))}
    </svg>
  );
}

